import { authorizedFetch } from "~/auth/authorizedFetch";
import { throwIfNotOk } from "./errorEnvelope";
import type {
  AuthorizationModel,
  BlueprintResponse,
  CheckRequest,
  CheckResponse,
  CreateResourceTypeRequest,
  CreateUsageRequest,
  ListModelsResponse,
  PagedResult,
  ReadTuplesRequest,
  ReadTuplesResponse,
  ResourceType,
  TupleKey,
  UpdateResourceTypeRequest,
  UpdateUsageRequest,
  Usage,
  ValidateUsagePlacementRequest,
  WriteTuplesRequest,
} from "./fga-types";

// The FGA API is bound server-side to the single access store, so no store id
// is ever sent from here. The dev proxy strips /fga-api, leaving /api for the backend.
const API_BASE = import.meta.env.VITE_FGA_API_URL || "/fga-api/api";

// Catalog endpoints moved to fga-management-api (ADR-0008).
const CATALOG_BASE =
  import.meta.env.VITE_MANAGEMENT_API_URL || "/management-api";

export async function getActiveModelId(): Promise<string | null> {
  const res = await authorizedFetch(`${API_BASE}/authorization-models/active`);
  await throwIfNotOk(res);
  const data = await res.json();
  return data.authorizationModelId ?? null;
}

export async function listModels(): Promise<ListModelsResponse> {
  const res = await authorizedFetch(`${API_BASE}/authorization-models`);
  await throwIfNotOk(res);
  return res.json();
}

export async function getModel(modelId: string): Promise<AuthorizationModel> {
  const res = await authorizedFetch(
    `${API_BASE}/authorization-models/${modelId}`,
  );
  await throwIfNotOk(res);
  return res.json();
}

export async function readTuples(
  request: ReadTuplesRequest,
): Promise<ReadTuplesResponse> {
  const res = await authorizedFetch(`${API_BASE}/tuples`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  await throwIfNotOk(res);
  return res.json();
}

export async function writeModel(
  modelJson: string,
): Promise<{ authorizationModelId: string }> {
  const res = await authorizedFetch(`${API_BASE}/authorization-models`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ modelJson }),
  });
  await throwIfNotOk(res);
  return res.json();
}

export async function writeTuples(request: WriteTuplesRequest): Promise<void> {
  const res = await authorizedFetch(`${API_BASE}/tuples/write`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  await throwIfNotOk(res);
}

export async function deleteTuples(deletes: TupleKey[]): Promise<void> {
  const res = await authorizedFetch(`${API_BASE}/tuples/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deletes }),
  });
  await throwIfNotOk(res);
}

export async function checkRelationship(
  request: CheckRequest,
): Promise<CheckResponse> {
  const res = await authorizedFetch(`${API_BASE}/check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });
  await throwIfNotOk(res);
  return res.json();
}

// --- Catalog ---

async function readJsonOrThrow<T>(res: Response): Promise<T> {
  await throwIfNotOk(res);
  return res.json() as Promise<T>;
}

export async function listResourceTypes(
  q?: string,
): Promise<PagedResult<ResourceType>> {
  const qs = q ? `?q=${encodeURIComponent(q)}` : "";
  const res = await authorizedFetch(
    `${CATALOG_BASE}/catalog/resource-types${qs}`,
  );
  return readJsonOrThrow(res);
}

export async function createResourceType(
  req: CreateResourceTypeRequest,
): Promise<ResourceType> {
  const res = await authorizedFetch(`${CATALOG_BASE}/catalog/resource-types`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
  return readJsonOrThrow(res);
}

export async function updateResourceType(
  id: string,
  req: UpdateResourceTypeRequest,
): Promise<ResourceType> {
  const res = await authorizedFetch(
    `${CATALOG_BASE}/catalog/resource-types/${id}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    },
  );
  return readJsonOrThrow(res);
}

export async function deleteResourceType(id: string): Promise<void> {
  const res = await authorizedFetch(
    `${CATALOG_BASE}/catalog/resource-types/${id}`,
    {
      method: "DELETE",
    },
  );
  await throwIfNotOk(res);
}

export async function createUsage(
  platformSlug: string,
  req: CreateUsageRequest,
): Promise<Usage> {
  const res = await authorizedFetch(
    `${CATALOG_BASE}/catalog/platforms/${encodeURIComponent(platformSlug)}/usages`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    },
  );
  return readJsonOrThrow(res);
}

// Runs the same placement checks a createUsage would (governance, qualified-name
// conflicts, sibling regex collisions, id-name-chain collisions) without persisting
// anything. Used before creating a not-yet-existing type, so a placement that would
// be rejected never leaves the type orphaned in the catalog. Resolves on success;
// throws IntilityIdException (with the human-readable reason) on failure.
export async function validateUsagePlacement(
  platformSlug: string,
  req: ValidateUsagePlacementRequest,
): Promise<void> {
  const res = await authorizedFetch(
    `${CATALOG_BASE}/catalog/platforms/${encodeURIComponent(platformSlug)}/usages/validate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    },
  );
  await throwIfNotOk(res);
}

export async function updateUsage(
  id: string,
  req: UpdateUsageRequest,
): Promise<Usage> {
  const res = await authorizedFetch(
    `${CATALOG_BASE}/catalog/usages/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    },
  );
  return readJsonOrThrow(res);
}

export async function deleteUsage(id: string): Promise<void> {
  const res = await authorizedFetch(`${CATALOG_BASE}/catalog/usages/${id}`, {
    method: "DELETE",
  });
  await throwIfNotOk(res);
}

// Every blueprint the caller may view, as defined in the Intility company.
export async function getBlueprints(): Promise<BlueprintResponse> {
  const res = await authorizedFetch(
    `${CATALOG_BASE}/catalog/platforms/blueprints`,
  );
  return readJsonOrThrow(res);
}
